import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/cn';
import {
  type CreateSkillRequest,
  CreateSkillRequestSchema,
  type SkillTool,
  type ToolVisibility,
} from '@innermate/shared';
import { useState } from 'react';
import { PairsWellWithCombo } from './PairsWellWithCombo';
import { ToolPicker } from './ToolPicker';

/**
 * Save / Cancel 버튼은 form 외부 (PageHeader actions) 에서 렌더된다.
 * 외부 버튼은 `form="admin-skill-form" type="submit"` 으로 이 폼을 submit 한다.
 * SkillForm 자체는 더 이상 Save/Cancel 액션을 그리지 않는다.
 */
export const SKILL_FORM_HTML_ID = 'admin-skill-form';

interface SkillFormProps {
  initial?: Partial<CreateSkillRequest>;
  slugLocked?: boolean;
  serverError?: string | null;
  /** body 외에 사용자가 입력한 changelog (선택) — update 흐름에서 새 버전 row 의 메모로 저장됨. */
  onSubmit: (body: CreateSkillRequest, opts: { changelog?: string }) => void;
  /**
   * true 면 form 이 dirty 한 상태에서 "변경 메모" textarea 가 노출됨.
   * 신규 생성 (CreateView) 에서는 false. 편집 (EditView) 에서는 true.
   */
  showChangelog?: boolean;
}

const DEFAULT_FORM: CreateSkillRequest = {
  slug: '',
  name: '',
  description: null,
  systemPrompt: '',
  toolVisibility: 'summary',
  enabled: true,
  tools: [],
  pairsWellWith: [],
};

const VISIBILITY_OPTIONS: Array<{
  value: ToolVisibility;
  label: string;
  description: string;
}> = [
  {
    value: 'summary',
    label: '요약',
    description: '툴 호출을 chip 한 줄로 요약해 노출. 입출력 상세는 숨김.',
  },
  {
    value: 'hidden',
    label: '숨김',
    description: '사용자 화면에 툴 호출을 표시하지 않음. 감사 로그에는 기록됨.',
  },
  {
    value: 'detailed',
    label: '상세',
    description: '툴 입력 / 출력 전체를 chip 펼침으로 노출. 디버깅용.',
  },
];

export function SkillForm({
  initial,
  slugLocked,
  serverError,
  onSubmit,
  showChangelog = false,
}: SkillFormProps) {
  const [form, setForm] = useState<CreateSkillRequest>({
    ...DEFAULT_FORM,
    ...initial,
    // normalise nullable fields: `undefined` from partial initial → proper defaults
    description: initial?.description ?? null,
  });
  const [changelog, setChangelog] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const update = <K extends keyof CreateSkillRequest>(key: K, value: CreateSkillRequest[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const onChangeTools = (tools: SkillTool[]) => {
    setForm((f) => ({ ...f, tools }));
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = CreateSkillRequestSchema.safeParse(form);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? '_');
        if (!next[key]) next[key] = issue.message;
      }
      setErrors(next);
      return;
    }
    setErrors({});
    const trimmedChangelog = changelog.trim();
    onSubmit(parsed.data, trimmedChangelog ? { changelog: trimmedChangelog } : {});
  };

  const promptLen = form.systemPrompt.length;
  const promptOver = promptLen > 10000;

  return (
    <form id={SKILL_FORM_HTML_ID} onSubmit={submit} className="space-y-6">
      {/* 기본 정보 */}
      <section className="rounded-xl border border-border bg-surface p-5 space-y-4">
        <h2 className="text-lg font-semibold">기본 정보</h2>

        <div className="grid grid-cols-[160px_1fr] gap-4">
          <Field
            label="slug"
            htmlFor="skill-slug"
            error={errors.slug}
            hint={slugLocked ? '생성 후 변경 불가' : 'URL-safe (a-z, 0-9, -)'}
          >
            <Input
              id="skill-slug"
              value={form.slug}
              disabled={slugLocked}
              onChange={(e) => update('slug', e.target.value)}
              placeholder="db-explorer"
            />
          </Field>
          <Field
            label="이름"
            htmlFor="skill-name"
            error={errors.name}
            hint="사용자에게 노출되는 이름"
          >
            <Input
              id="skill-name"
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              placeholder="DB 분석가"
            />
          </Field>
        </div>

        <Field
          label="설명 (선택)"
          htmlFor="skill-description"
          error={errors.description}
          hint="사용자가 Skill 을 고를 때 보는 짧은 설명."
        >
          <textarea
            id="skill-description"
            value={form.description ?? ''}
            onChange={(e) =>
              update('description', e.target.value.length === 0 ? null : e.target.value)
            }
            rows={2}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus-visible:outline-none"
            placeholder="등록된 데이터베이스에 대해 자연어로 분석 질문에 답합니다."
          />
        </Field>

        <Field label="활성 상태" htmlFor="skill-enabled">
          <label className="flex items-center gap-2 text-sm select-none">
            <input
              id="skill-enabled"
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => update('enabled', e.target.checked)}
              className="size-4 accent-accent"
            />
            <span>사용자 드롭다운에 노출 (비활성화하면 기존 세션도 읽기 전용)</span>
          </label>
        </Field>

        <Field
          label="cross-domain 동료 skill (선택)"
          htmlFor="skill-pairs-well-with"
          error={errors.pairsWellWith}
          hint="이 skill 과 자주 함께 ranked 되어야 하는 동료 skill 을 선택. 타이핑하면 자동완성, ↑↓ Enter 로 선택, Backspace 로 마지막 항목 제거. classifier 가 두 역할을 동시 활성하도록 hint 가 됩니다."
        >
          <PairsWellWithCombo
            value={form.pairsWellWith ?? []}
            onChange={(next) => update('pairsWellWith', next)}
            ownSlug={form.slug}
          />
        </Field>
      </section>

      {/* 툴 호출 노출 */}
      <section className="rounded-xl border border-border bg-surface p-5 space-y-3">
        <h2 className="text-lg font-semibold">툴 호출 노출</h2>
        <p className="text-xs text-foreground-muted">
          이 Skill 이 툴을 호출할 때 사용자 화면에 어떻게 표시할지. 사용자별로 override 가능
          (디버깅용).
        </p>
        <div className="grid grid-cols-3 gap-3">
          {VISIBILITY_OPTIONS.map((opt) => {
            const selected = form.toolVisibility === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => update('toolVisibility', opt.value)}
                className={cn(
                  'text-left rounded-lg border p-3 transition',
                  selected
                    ? 'border-accent bg-accent/5'
                    : 'border-border bg-surface hover:border-accent/40',
                )}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      'inline-block size-3 rounded-full border',
                      selected ? 'border-accent bg-accent' : 'border-border',
                    )}
                  />
                  <span className="font-medium text-sm">{opt.label}</span>
                </div>
                <p className="mt-1.5 text-xs text-foreground-muted">{opt.description}</p>
              </button>
            );
          })}
        </div>
        {errors.toolVisibility ? (
          <p className="text-xs text-destructive">{errors.toolVisibility}</p>
        ) : null}
      </section>

      {/* 시스템 프롬프트 */}
      <section className="rounded-xl border border-border bg-surface p-5 space-y-2">
        <div className="flex items-baseline justify-between">
          <h2 className="text-lg font-semibold">시스템 프롬프트</h2>
          <span
            className={cn('text-xs', promptOver ? 'text-destructive' : 'text-foreground-muted')}
          >
            {promptLen.toLocaleString()}/10,000
          </span>
        </div>
        <p className="text-xs text-foreground-muted">
          이 Skill 이 바인딩된 세션에서 대화 시작 시 system 메시지로 주입됩니다.
        </p>
        <textarea
          id="skill-system-prompt"
          value={form.systemPrompt}
          onChange={(e) => update('systemPrompt', e.target.value)}
          rows={10}
          className="w-full font-mono text-xs rounded-md border border-border bg-surface px-3 py-2 focus-visible:outline-none min-h-60"
          placeholder={
            '등록된 데이터베이스에 읽기 전용으로 질의해 답한다.\ndb.list_tables → db.describe_table → db.run_query 순으로 호출한다.\n결과는 표로 요약한다.'
          }
        />
        {errors.systemPrompt ? (
          <p className="text-xs text-destructive">{errors.systemPrompt}</p>
        ) : null}
      </section>

      {/* 툴 */}
      <section className="rounded-xl border border-border bg-surface p-5 space-y-3">
        <div>
          <h2 className="text-lg font-semibold">사용할 툴</h2>
          <p className="text-xs text-foreground-muted">
            체크한 툴만 LLM 에게 function tool 로 노출됩니다. 각 툴 별 설정은 체크 시 펼쳐집니다.
          </p>
        </div>
        <ToolPicker tools={form.tools} onChange={onChangeTools} />
        {errors.tools ? <p className="text-xs text-destructive">{errors.tools}</p> : null}
      </section>

      {serverError ? (
        <div
          role="alert"
          className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {serverError}
        </div>
      ) : null}

      {showChangelog ? (
        <Field
          label="변경 메모 (선택)"
          htmlFor="skill-changelog"
          hint="이번 저장의 변경 이유나 맥락을 한 줄로. 버전 히스토리에 함께 보존되어 나중에 어떤 변경이었는지 추적 가능. 빈 채로 두면 자동 source 태그만 기록."
        >
          <textarea
            id="skill-changelog"
            value={changelog}
            onChange={(e) => setChangelog(e.target.value)}
            rows={2}
            maxLength={500}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus-visible:outline-none"
            placeholder="예: 매출 정의 변경 반영 / pairsWellWith 보강 / 권한 문구 명확화"
          />
        </Field>
      ) : null}
    </form>
  );
}
